import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, AlertCircle, Loader2, ArrowLeft } from "lucide-react";
import SubpageLayout from "@/components/SubpageLayout";
import Embers from "@/components/Embers";
import { formatHUF } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WebshopSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", payment_status: null, amount_total: null, currency: null, metadata: null });
  const attemptsRef = useRef(0);

  useEffect(() => {
    document.title = "Sikeres fizetés · Pakkaland";
    if (!sessionId) {
      setState({ status: "error", payment_status: null });
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const r = await axios.get(`${API}/checkout/status/${sessionId}`);
        const data = r.data;
        if (data.payment_status === "paid") {
          setState({ status: "paid", ...data });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired", ...data });
          return;
        }
      } catch (e) {
        console.error(e);
      }
      if (attemptsRef.current >= 6) {
        setState((s) => ({ ...s, status: "timeout" }));
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const isPaid = state.status === "paid";
  const isError = state.status === "error" || state.status === "expired";

  return (
    <SubpageLayout testId="page-webshop-success">
      <section className="relative min-h-[80vh] bg-pkcharcoal overflow-hidden flex items-center pt-24 pb-20">
        <div
          className="absolute inset-0 pointer-events-none opacity-40"
          style={{
            background: "radial-gradient(ellipse at 50% 50%, rgba(217,4,41,0.4) 0%, transparent 60%)",
          }}
        />
        <Embers count={16} />
        <div className="relative max-w-2xl mx-auto px-5 sm:px-8 text-center w-full">
          {state.status === "polling" && (
            <>
              <Loader2 className="w-10 h-10 text-pkred animate-spin mx-auto" strokeWidth={1.5} />
              <h1 data-testid="success-title" className="font-serif text-white text-4xl sm:text-5xl mt-8 leading-tight">
                Fizetés ellenőrzése<span className="text-pkred">…</span>
              </h1>
              <p className="text-white/70 mt-4">
                Egy pillanat — a Stripe-tól kérjük a megerősítést.
              </p>
            </>
          )}
          {isPaid && (
            <>
              <CheckCircle2 className="w-14 h-14 text-pkred mx-auto" strokeWidth={1.5} />
              <span className="block mt-6 text-[0.65rem] tracking-[0.4em] uppercase text-pkred font-semibold">
                Sikeres rendelés
              </span>
              <h1 data-testid="success-title" className="font-serif text-white text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.05]">
                Köszönjük<span className="text-pkred">,</span><br />
                <span className="italic text-pkred">találkozunk Vezsenyen.</span>
              </h1>
              <p className="text-white/75 mt-6 text-base sm:text-lg leading-relaxed">
                A megrendelésed beérkezett. A részletekről emailt küldtünk a Stripe-on
                keresztül, és hamarosan jelentkezünk a deszka egyeztetésével.
              </p>
              {state.amount_total != null && state.currency && (
                <div className="mt-8 inline-flex items-baseline gap-3 font-serif">
                  <span className="text-pkred text-3xl sm:text-4xl">
                    {state.currency === "huf"
                      ? formatHUF(Math.round(state.amount_total / 100))
                      : `${(state.amount_total / 100).toFixed(2)} ${state.currency.toUpperCase()}`}
                  </span>
                  {state.metadata?.product_name && (
                    <span className="text-white/55 text-sm tracking-[0.2em] uppercase">
                      · {state.metadata.product_name}
                    </span>
                  )}
                </div>
              )}
            </>
          )}
          {isError && (
            <>
              <AlertCircle className="w-14 h-14 text-pkred mx-auto" strokeWidth={1.5} />
              <h1 className="font-serif text-white text-4xl sm:text-5xl mt-8 leading-tight">
                Valami félrement<span className="text-pkred">.</span>
              </h1>
              <p className="text-white/70 mt-4">
                A fizetés nem zárult le. Próbáld újra, vagy keress minket emailben.
              </p>
            </>
          )}
          {state.status === "timeout" && (
            <>
              <AlertCircle className="w-14 h-14 text-pkred mx-auto" strokeWidth={1.5} />
              <h1 className="font-serif text-white text-4xl sm:text-5xl mt-8 leading-tight">
                Még feldolgozás alatt<span className="text-pkred">.</span>
              </h1>
              <p className="text-white/70 mt-4">
                A státusz még nem érkezett meg. Nézz vissza pár perc múlva, vagy ellenőrizd az emailedet.
              </p>
            </>
          )}
          <Link
            to="/vagodeszkak"
            data-testid="success-back"
            className="btn-ghost inline-flex mt-12"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.8} />
            Vissza a deszkákhoz
          </Link>
        </div>
      </section>
    </SubpageLayout>
  );
}
